import { useState } from "react";
import { generateEditUrl } from '../../../core/utils/urlUtils';
import { apiFetch } from "../../../core/utils/api";
import logger from '../../../core/utils/logger';
import { normalizeSubdomain } from "../../../core/utils/subdomainUtils";
import { DEFAULT_CREATE_PAGE_DATA, API_ENDPOINTS } from "../constants";

/**
 * Custom hook to manage page creation state and logic
 */
export const usePageCreation = (website, websiteId, username, domain, version_slug) => {
  const [createPageData, setCreatePageData] = useState(DEFAULT_CREATE_PAGE_DATA);
  const [isCreatingPage, setIsCreatingPage] = useState(false);
  const [createPageError, setCreatePageError] = useState('');

  const hasNavbar = website?.navbar;
  const hasLeftSidebar = website?.left_sidebar;
  const hasRightSidebar = website?.right_sidebar;

  // Function to create proper defaults based on current navigation state
  const getNavigationDefaults = () => ({
    name: '',
    subdomain: '',
    // For existing navigation, default to adding to them
    addToNavbar: hasNavbar,
    addToLeftSidebar: hasLeftSidebar,
    addToRightSidebar: hasRightSidebar,
    // For non-existing navigation, default to not creating them (except navbar)
    createNavbar: !hasNavbar,
    createLeftSidebar: false,
    createRightSidebar: false
  });

  const handleCreatePageSubmit = async (e) => {
    e.preventDefault();
    
    logger.info(`[PAGE_CREATE] ========== STARTING PAGE CREATION ==========`);
    logger.info(`[PAGE_CREATE] Form submitted with page name: "${createPageData.name}" and subdomain: "${createPageData.subdomain}"`);
    logger.logData('INFO', '[PAGE_CREATE] Full createPageData', createPageData);
    logger.info(`[PAGE_CREATE] Current navigation state - hasNavbar: ${hasNavbar}, hasLeftSidebar: ${hasLeftSidebar}, hasRightSidebar: ${hasRightSidebar}`);
    
    if (isCreatingPage) {
      logger.warn(`[PAGE_CREATE] Already creating page, ignoring duplicate request`);
      return;
    }
    
    if (!createPageData.name.trim()) {
      logger.error(`[PAGE_CREATE] Validation failed - missing name`);
      setCreatePageError('Page name is required');
      return;
    }

    setIsCreatingPage(true);
    setCreatePageError('');
    logger.info(`[PAGE_CREATE] Starting page creation process...`);

    try {
      logger.info(`[PAGE_CREATE] Making API call to create page: ${API_ENDPOINTS.PAGE_CREATE(websiteId)}`);
      // If a template is chosen, use the template-based endpoint
      const useTemplate = createPageData.template && createPageData.template.trim() !== '';
      const endpoint = useTemplate ? API_ENDPOINTS.PAGE_CREATE_FROM_TEMPLATE(websiteId) : API_ENDPOINTS.PAGE_CREATE(websiteId);
      const normalizedSubdomain = normalizeSubdomain(createPageData.subdomain);
      const payload = useTemplate ? {
        template: createPageData.template,
        name: createPageData.name,
        subdomain: normalizedSubdomain,
      } : {
        name: createPageData.name,
        subdomain: normalizedSubdomain,
      };

      const pageResponse = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      logger.info(`[PAGE_CREATE] Page creation response status: ${pageResponse.status}`);

      if (!pageResponse.ok) {
        const errorData = await pageResponse.json();
        logger.error(`[PAGE_CREATE] Page creation failed with error: ${JSON.stringify(errorData)}`);
        
        // Handle validation errors with specific field information
        // DRF returns validation errors as arrays, so we need to extract the first message
        if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          throw new Error(errorData.non_field_errors[0]);
        } else if (errorData.non_field_errors) {
          throw new Error(errorData.non_field_errors);
        } else if (errorData.subdomain && Array.isArray(errorData.subdomain)) {
          throw new Error(errorData.subdomain[0]);
        } else if (errorData.subdomain) {
          throw new Error(errorData.subdomain);
        } else if (errorData.name && Array.isArray(errorData.name)) {
          throw new Error(errorData.name[0]);
        } else if (errorData.name) {
          throw new Error(errorData.name);
        } else {
          throw new Error(errorData.error || 'Failed to create page');
        }
      }

      const newPage = await pageResponse.json();
      logger.logData('INFO', '[PAGE_CREATE] Page created successfully', newPage);
      const pageSubdomain = normalizedSubdomain;
      
      // Store the original navigation configuration before resetting
      const originalNavConfig = {
        addToNavbar: createPageData.addToNavbar,
        addToLeftSidebar: createPageData.addToLeftSidebar,
        addToRightSidebar: createPageData.addToRightSidebar,
        createNavbar: createPageData.createNavbar,
        createLeftSidebar: createPageData.createLeftSidebar,
        createRightSidebar: createPageData.createRightSidebar,
        pageName: createPageData.name
      };
      
      setCreatePageData(getNavigationDefaults());
      
      let navbarId = website?.navbar?.id;
      let leftSidebarId = website?.left_sidebar?.id;
      let rightSidebarId = website?.right_sidebar?.id;

      if (!hasNavbar && originalNavConfig.createNavbar) {
        try {
          const navbarResponse = await apiFetch(API_ENDPOINTS.NAVBAR_UPDATE(websiteId), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ section_position: 'left' }),
          });
          
          if (navbarResponse.ok) {
            const navbarData = await navbarResponse.json();
            navbarId = navbarData.id;
          }
        } catch (navError) {
          console.warn('Failed to create navbar:', navError);
        }
      }

      if (!hasLeftSidebar && originalNavConfig.createLeftSidebar) {
        try {
          const sidebarResponse = await apiFetch(API_ENDPOINTS.LEFTSIDEBAR_UPDATE(websiteId), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ side: 'left' }),
          });
          
          if (sidebarResponse.ok) {
            const sidebarData = await sidebarResponse.json();
            leftSidebarId = sidebarData.id;
          }
        } catch (sidebarError) {
          console.warn('Failed to create left sidebar:', sidebarError);
        }
      }

      if (!hasRightSidebar && originalNavConfig.createRightSidebar) {
        try {
          const sidebarResponse = await apiFetch(API_ENDPOINTS.RIGHTSIDEBAR_UPDATE(websiteId), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ side: 'right' }),
          });
          
          if (sidebarResponse.ok) {
            const sidebarData = await sidebarResponse.json();
            rightSidebarId = sidebarData.id;
          }
        } catch (sidebarError) {
          console.warn('Failed to create right sidebar:', sidebarError);
        }
      }

      const shouldAddToNavbar = (hasNavbar && originalNavConfig.addToNavbar) || 
                               (!hasNavbar && originalNavConfig.createNavbar);
      
      logger.info(`[PAGE_CREATE] Should add to navbar: ${shouldAddToNavbar}, navbarId: ${navbarId}`);
      logger.info(`[PAGE_CREATE] hasNavbar: ${hasNavbar}, addToNavbar: ${originalNavConfig.addToNavbar}, createNavbar: ${originalNavConfig.createNavbar}`);
      
      const shouldAddToLeftSidebar = (hasLeftSidebar && originalNavConfig.addToLeftSidebar) || 
                                     (!hasLeftSidebar && originalNavConfig.createLeftSidebar);

      logger.info(`[PAGE_CREATE] Should add to left sidebar: ${shouldAddToLeftSidebar}, leftSidebarId: ${leftSidebarId}`);

      const shouldAddToRightSidebar = (hasRightSidebar && originalNavConfig.addToRightSidebar) || 
                                     (!hasRightSidebar && originalNavConfig.createRightSidebar);

      logger.info(`[PAGE_CREATE] Should add to right sidebar: ${shouldAddToRightSidebar}, rightSidebarId: ${rightSidebarId}`);

      // Create all navigation sections in parallel and wait for completion
      const navigationPromises = [];

      if (shouldAddToNavbar && navbarId) {
        const navbarData = {
          label: { content: originalNavConfig.pageName, type: "text" },
          link_choice: "internal",
          link_internal: newPage.page.id,
          order: (website?.navbar?.sections?.length || 0) + 1,
        };
        logger.logData('INFO', '[PAGE_CREATE] Adding page to navbar with data', navbarData);
        
        const navbarPromise = apiFetch(API_ENDPOINTS.NAVBAR_SECTIONS_CREATE(navbarId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(navbarData),
        }).then(async (response) => {
          logger.info(`[PAGE_CREATE] Navbar section creation response status: ${response.status}`);
          
          if (!response.ok) {
            const errorData = await response.json();
            logger.logData('ERROR', '[PAGE_CREATE] Navbar section creation failed', errorData);
            throw new Error(`Navbar section creation failed: ${JSON.stringify(errorData)}`);
          } else {
            const responseData = await response.json();
            logger.logData('INFO', '[PAGE_CREATE] Navbar section created successfully', responseData);
            return responseData;
          }
        });
        
        navigationPromises.push(navbarPromise);
      }

      if (shouldAddToLeftSidebar && leftSidebarId) {
        const leftSidebarData = {
          label: { content: originalNavConfig.pageName, type: "text" },
          link_choice: "internal",
          link_internal: newPage.page.id,
          order: (website?.left_sidebar?.sections?.length || 0) + 1,
        };
        logger.logData('INFO', '[PAGE_CREATE] Adding page to left sidebar with data', leftSidebarData);
        
        const leftSidebarPromise = apiFetch(API_ENDPOINTS.LEFTSIDEBAR_SECTIONS_CREATE(leftSidebarId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(leftSidebarData),
        }).then(async (response) => {
          logger.info(`[PAGE_CREATE] Left sidebar section creation response status: ${response.status}`);
          
          if (!response.ok) {
            const errorData = await response.json();
            logger.logData('ERROR', '[PAGE_CREATE] Left sidebar section creation failed', errorData);
            throw new Error(`Left sidebar section creation failed: ${JSON.stringify(errorData)}`);
          } else {
            const responseData = await response.json();
            logger.logData('INFO', '[PAGE_CREATE] Left sidebar section created successfully', responseData);
            return responseData;
          }
        });
        
        navigationPromises.push(leftSidebarPromise);
      }

      if (shouldAddToRightSidebar && rightSidebarId) {
        const rightSidebarData = {
          label: { content: originalNavConfig.pageName, type: "text" },
          link_choice: "internal",
          link_internal: newPage.page.id,
          order: (website?.right_sidebar?.sections?.length || 0) + 1,
        };
        logger.logData('INFO', '[PAGE_CREATE] Adding page to right sidebar with data', rightSidebarData);
        
        const rightSidebarPromise = apiFetch(API_ENDPOINTS.RIGHTSIDEBAR_SECTIONS_CREATE(rightSidebarId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(rightSidebarData),
        }).then(async (response) => {
          logger.info(`[PAGE_CREATE] Right sidebar section creation response status: ${response.status}`);
          
          if (!response.ok) {
            const errorData = await response.json();
            logger.logData('ERROR', '[PAGE_CREATE] Right sidebar section creation failed', errorData);
            throw new Error(`Right sidebar section creation failed: ${JSON.stringify(errorData)}`);
          } else {
            const responseData = await response.json();
            logger.logData('INFO', '[PAGE_CREATE] Right sidebar section created successfully', responseData);
            return responseData;
          }
        });
        
        navigationPromises.push(rightSidebarPromise);
      }

      // Wait for all navigation section creation to complete
      if (navigationPromises.length > 0) {
        logger.info(`[PAGE_CREATE] Waiting for ${navigationPromises.length} navigation sections to be created...`);
        const results = await Promise.allSettled(navigationPromises);
        
        const failures = results.filter(result => result.status === 'rejected');
        if (failures.length > 0) {
          logger.error(`[PAGE_CREATE] ${failures.length} navigation sections failed to create:`, failures);
          // Still redirect but show a warning
          alert(`Page created successfully, but some navigation links may not have been added. Please check the navbar/sidebar.`);
        } else {
          logger.info(`[PAGE_CREATE] All navigation sections created successfully`);
        }
      }

      const newPageUrl = generateEditUrl(username, domain, version_slug, pageSubdomain);
      logger.info(`[PAGE_CREATE] Redirecting to: ${newPageUrl}`);
      window.location.href = newPageUrl;

    } catch (error) {
      logger.error(`[PAGE_CREATE] Page creation failed with error: ${error.message}`);
      logger.logData('ERROR', '[PAGE_CREATE] Full error details', { 
        message: error.message, 
        stack: error.stack,
        createPageData: createPageData 
      });
      setCreatePageError(error.message || 'Failed to create page');
    } finally {
      setIsCreatingPage(false);
      logger.info(`[PAGE_CREATE] ========== PAGE CREATION FINISHED ==========`);
    }
  };

  const handleCreatePageCancel = () => {
    setCreatePageError('');
    setCreatePageData(getNavigationDefaults());
  };

  return {
    createPageData,
    setCreatePageData,
    isCreatingPage,
    createPageError,
    setCreatePageError,
    hasNavbar,
    hasLeftSidebar,
    hasRightSidebar,
    getNavigationDefaults,
    handleCreatePageSubmit,
    handleCreatePageCancel
  };
};

